import type { KanhaChatConfig, KanhaChatMetrics, RetrievalEvent } from "./types";
import { unwrapSystemPrompt, type RankedGroundedChunk } from "./grounded-corpus";

export const MODEL_ID = "kanha-custom-model";
export const MINICPM5_SYSTEM_PROMPT =
  "Answer only from the supplied context. Be concise. If the answer is absent from the context, respond exactly: I can't answer that from the provided context.";
const GENERATED_REFUSAL_SYSTEM_PROMPT = "I’m sorry, but I can’t comply with that request.";

const PREBUILT_MODEL_BY_SIZE: Record<string, string> = {
  small: "Qwen3-0.6B-q4f16_1-MLC",
  medium: "Qwen3-1.7B-q4f16_1-MLC",
  large: "Qwen3-4B-q4f16_1-MLC",
};

const MINICPM5_1B_MODEL_URL =
  "https://huggingface.co/Kanha-AI/kanha-minicpm5-1b-grounded-matched-2ep-20260910-MLC/resolve/2aa9d7bb2b8b2909d2a21661bc1b109dac228678/";
const MINICPM5_2B_MODEL_URL =
  "https://huggingface.co/Kanha-AI/kanha-minicpm5-2b-grounded-matched-2ep-20260910-MLC/resolve/60eab22c37f82f68f214f67ba0d53a79f50b4950/";

const BUILT_IN_ARTIFACTS_BY_SIZE: Record<string, { modelUrl: string; modelLib: string }> = {
  "minicpm5-1b": {
    modelUrl: MINICPM5_1B_MODEL_URL,
    modelLib: `${MINICPM5_1B_MODEL_URL}minicpm5-1b-q4f16_1-webgpu.wasm`,
  },
  "minicpm5-2b": {
    modelUrl: MINICPM5_2B_MODEL_URL,
    modelLib: `${MINICPM5_2B_MODEL_URL}minicpm5-2b-q4f16_1-webgpu.wasm`,
  },
};

const MIN_RAM_GB: Record<string, number> = {
  small: 2,
  medium: 4,
  large: 8,
  "minicpm5-1b": 4,
  "minicpm5-2b": 6,
};

type WebLLM = typeof import("@mlc-ai/web-llm");

export async function checkWebGPU(): Promise<boolean> {
  try {
    const nav = navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } };
    return !!nav.gpu && !!(await nav.gpu.requestAdapter());
  } catch {
    return false;
  }
}

export function checkAvailableMemory(config: KanhaChatConfig): string | null {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const required = config.minRamGb ?? MIN_RAM_GB[config.modelSize ?? "small"] ?? MIN_RAM_GB.small;
  if (nav.deviceMemory && nav.deviceMemory < 8 && nav.deviceMemory < required) {
    const modelSize = config.modelSize ?? "small";
    return `Your device has ~${nav.deviceMemory}GB RAM but the ${modelSize} model needs at least ${required}GB.`;
  }
  return null;
}

export function resolveModelLib(webllm: WebLLM, config: KanhaChatConfig): string {
  if (config.modelLib) return config.modelLib;
  const size = config.modelSize ?? "small";
  const builtIn = BUILT_IN_ARTIFACTS_BY_SIZE[size];
  if (builtIn) return builtIn.modelLib;
  const modelId = PREBUILT_MODEL_BY_SIZE[size];
  const modelLib = webllm.prebuiltAppConfig.model_list.find((model) => model.model_id === modelId)?.model_lib;
  if (!modelLib) throw new Error(`Unknown model size "${size}". Provide modelLib explicitly.`);
  return modelLib;
}

export function toWebLLMModelUrl(modelUrl: string): string {
  let url = modelUrl.endsWith("/") ? modelUrl.slice(0, -1) : modelUrl;
  if (!url.includes("/resolve/")) url += "/resolve/main";
  return url;
}
export function createAppConfig(webllm: WebLLM, config: KanhaChatConfig) {
  const modelLib = resolveModelLib(webllm, config);

  const size = config.modelSize ?? "small";
  const modelId = PREBUILT_MODEL_BY_SIZE[size];
  const prebuiltModel = webllm.prebuiltAppConfig.model_list.find((model) => model.model_id === modelId);
  const artifactUrl = config.modelUrl || BUILT_IN_ARTIFACTS_BY_SIZE[size]?.modelUrl;

  const finalModelUrl = artifactUrl
    ? toWebLLMModelUrl(artifactUrl)
    : prebuiltModel?.model;

  if (!finalModelUrl) {
    throw new Error(`Failed to resolve model URL for size "${size}".`);
  }

  return {
    model_list: [{
      model: finalModelUrl,
      model_id: MODEL_ID,
      model_lib: modelLib,
      overrides: { context_window_size: config.contextWindowSize ?? 4096 },
    }],
    useIndexedDBCache: (config.cacheBackend ?? "indexeddb") === "indexeddb",
  };
}

export function describeLoadStage(text: string | undefined): string {
  const report = text?.trim();
  if (!report) return "Loading model";
  if (/from cache/i.test(report)) return "Loading cached model";
  if (/fetch|download/i.test(report)) return "Downloading model";
  if (/finish/i.test(report)) return "Almost ready";
  if (/shader|gpu|compil/i.test(report)) return "Preparing GPU";
  return report;
}

export async function createEngine(
  webllm: WebLLM,
  config: KanhaChatConfig,
  initProgressCallback: (report: { progress: number; text?: string }) => void,
) {
  const appConfig = createAppConfig(webllm, config);
  if (config.modelUrl || BUILT_IN_ARTIFACTS_BY_SIZE[config.modelSize ?? "small"]) {
    const cached = await webllm.hasModelInCache(MODEL_ID, appConfig).catch(() => false);
    if (!cached) {
      const storage = await checkStorageForModel(appConfig.model_list[0].model);
      if (storage && !hasEnoughStorage(storage)) {
        throw new InsufficientStorageError(
          describeInsufficientStorage(storage.requiredBytes, storage.availableBytes),
        );
      }
    }
  }
  if (config.workerUrl) {
    const worker = new Worker(config.workerUrl, { type: "module" });

    try {
      const engine = await webllm.CreateWebWorkerMLCEngine(worker, MODEL_ID, {
        appConfig,
        initProgressCallback,
        logLevel: "WARN",
      });
      return { engine, worker };
    } catch (error) {
      worker.terminate();
      throw error;
    }
  }

  const engine = await webllm.CreateMLCEngine(MODEL_ID, {
    appConfig,
    initProgressCallback,
    logLevel: "WARN",
  });
  return { engine, worker: null };
}

export async function disposeEngine(engine: unknown, worker: Worker | null): Promise<void> {
  try {
    await (engine as { unload(): Promise<void> }).unload();
  } catch {
  }
  worker?.terminate();
}

export function toMetrics(
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    extra?: {
      e2e_latency_s?: number;
      time_to_first_token_s?: number;
      prefill_tokens_per_s?: number;
      decode_tokens_per_s?: number;
    };
  },
  config: KanhaChatConfig,
): KanhaChatMetrics {
  return {
    promptTokens: usage.prompt_tokens,
    completionTokens: usage.completion_tokens,
    totalTokens: usage.total_tokens,
    e2eLatencyMs: (usage.extra?.e2e_latency_s ?? 0) * 1000,
    timeToFirstTokenMs: (usage.extra?.time_to_first_token_s ?? 0) * 1000,
    prefillTokensPerSecond: usage.extra?.prefill_tokens_per_s ?? 0,
    decodeTokensPerSecond: usage.extra?.decode_tokens_per_s ?? 0,
    modelSize: config.modelSize ?? "small",
    modelId: MODEL_ID,
    timestamp: new Date().toISOString(),
  };
}

export function emitMetrics(
  usage: Parameters<typeof toMetrics>[0],
  config: KanhaChatConfig,
): void {
  try {
    config.onMetrics?.(toMetrics(usage, config));
  } catch {
  }
}

export function retrievalChunks(ranked: RankedGroundedChunk[]): RetrievalEvent["chunks"] {
  return ranked.map(({ chunk, chunkIndex, score, bm25Score, denseScore, rerankerScore }) => ({
    chunkId: chunk.chunk_id,
    documentId: chunk.document_id,
    chunkIndex: chunkIndex ?? null,
    title: chunk.title,
    url: chunk.url,
    score,
    bm25Score: bm25Score ?? null,
    denseScore: denseScore ?? null,
    rerankerScore: rerankerScore ?? null,
  }));
}

export function emitRetrieval(config: KanhaChatConfig, event: RetrievalEvent): void {
  console.debug("[KanhaBot] retrieval", event);
  try {
    config.onRetrieval?.(event);
  } catch {}
}

export async function resetEngine(engine: unknown): Promise<void> {
  await (engine as { resetChat(): Promise<void> }).resetChat();
}

export async function resetEngineBestEffort(engine: unknown): Promise<void> {
  try {
    await resetEngine(engine);
  } catch {
  }
}

const MINICPM5_MODEL_LIB = /\/minicpm5-(?:1b|2b)-[^/]*\.wasm$/i;

export function usesMiniCPMModel(config: KanhaChatConfig): boolean {
  return config.modelSize?.startsWith("minicpm5-") === true
    || MINICPM5_MODEL_LIB.test((config.modelLib ?? "").split(/[?#]/)[0]);
}

export function usesPrefilledMiniCPMThinking(config: KanhaChatConfig): boolean {
  return config.enableThinking === true && usesMiniCPMModel(config);
}

export function resolveSystemPrompt(config: KanhaChatConfig): string | undefined {
  const custom = unwrapSystemPrompt(config.systemPrompt);
  if (custom && custom !== GENERATED_REFUSAL_SYSTEM_PROMPT) return custom;
  return usesMiniCPMModel(config) ? MINICPM5_SYSTEM_PROMPT : undefined;
}

export function stripThinkTokens(text: string, startsInsideThinking = false): string {
  if (startsInsideThinking && !text.includes("</think>")) return "";
  if (startsInsideThinking && !text.includes("<think>")) {
    text = text.slice(text.indexOf("</think>") + "</think>".length);
  }
  let cleaned = text.replace(/<think>[\s\S]*?<\/think>/g, "");
  cleaned = cleaned.replace(/<think>[\s\S]*$/g, "");
  return cleaned.trimStart();
}

const MIN_FREE_STORAGE_BYTES = 1.5 * 1024 ** 3;

export function isMemoryError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("out of memory") ||
    message.includes("oom") ||
    message.includes("allocation failed") ||
    message.includes("arraybuffer") ||
    message.includes("could not allocate") ||
    message.includes("webgpu device lost")
  );
}

function isStorageError(error: unknown): boolean {
  return error instanceof Error && /quota|storage|indexeddb|cache/i.test(error.message);
}

export async function freeStorageBytes(): Promise<number | null> {
  try {
    const nav = typeof navigator === "undefined" ? undefined : navigator;
    const estimate = await nav?.storage?.estimate?.();
    if (estimate?.quota === undefined || estimate.usage === undefined) return null;
    return estimate.quota - estimate.usage;
  } catch {
    return null;
  }
}

const STORAGE_HEADROOM_BYTES = 256 * 1024 ** 2;

export class InsufficientStorageError extends Error {}

export interface StorageCheck {
  requiredBytes: number;
  availableBytes: number;
}

export function sumTensorCacheBytes(payload: unknown): number | null {
  const records = (payload as { records?: unknown } | null | undefined)?.records;
  if (!Array.isArray(records) || records.length === 0) return null;
  let total = 0;
  for (const record of records) {
    const nbytes = (record as { nbytes?: unknown } | null | undefined)?.nbytes;
    if (typeof nbytes !== "number" || !Number.isFinite(nbytes) || nbytes < 0) return null;
    total += nbytes;
  }
  return total;
}

export async function checkStorageForModel(modelUrl: string): Promise<StorageCheck | null> {
  try {
    const base = modelUrl.endsWith("/") ? modelUrl.slice(0, -1) : modelUrl;
    const response = await fetch(`${base}/tensor-cache.json`);
    if (!response.ok) return null;
    const requiredBytes = sumTensorCacheBytes(await response.json());
    if (requiredBytes === null) return null;
    const availableBytes = await freeStorageBytes();
    if (availableBytes === null) return null;
    return { requiredBytes, availableBytes };
  } catch {
    return null;
  }
}

export function hasEnoughStorage(
  check: StorageCheck | null,
  headroomBytes = STORAGE_HEADROOM_BYTES,
): boolean {
  if (!check) return true;
  return check.availableBytes >= check.requiredBytes + headroomBytes;
}

export function describeInsufficientStorage(required: number, available: number): string {
  const requiredGb = (Math.max(required, 0) / 1024 ** 3).toFixed(1);
  const availableGb = (Math.max(available, 0) / 1024 ** 3).toFixed(1);
  return `This assistant needs about ${requiredGb} GB of free browser storage to download its model, but only about ${availableGb} GB is available. Free up space or use another browser, then reload.`;
}

export async function describeLoadError(error: unknown, config: KanhaChatConfig): Promise<string> {
  if (error instanceof InsufficientStorageError) return error.message;
  if (isMemoryError(error)) {
    const size = config.modelSize ?? "small";
    const nav = typeof navigator === "undefined"
      ? undefined
      : (navigator as Navigator & { deviceMemory?: number });
    const ram = nav?.deviceMemory;
    return `Not enough memory to load the ${size} model${ram ? ` (device has ~${ram}GB RAM)` : ""}. Close other tabs or try a smaller model.`;
  }
  const free = await freeStorageBytes();
  if (isStorageError(error)) {
    const freeText = free === null ? "" : ` (about ${(free / 1024 ** 3).toFixed(1)} GB free)`;
    return `Your browser is low on storage for this site${freeText}. Free up disk space or clear this site's data, then reload.`;
  }
  const message = error instanceof Error && error.message ? error.message : "Failed to load AI model.";
  if (free !== null && free < MIN_FREE_STORAGE_BYTES) {
    const freeGb = (Math.max(free, 0) / 1024 ** 3).toFixed(1);
    return `${message} Your browser also has only about ${freeGb} GB of storage free for this site, which can make model downloads fail.`;
  }
  return message;
}
